import { History } from "history";
import * as _ from "lodash";
import { inject, observer } from "mobx-react";
import React, { Component, createRef, forwardRef, MouseEvent, useEffect, useImperativeHandle, Fragment, useMemo, useState } from "react";
import { Badge, Button, Col, Dropdown, Modal, Row, Spinner, Table } from "react-bootstrap";
import { NavLink, NavigateFunction, Location, useNavigate, useLocation } from "react-router-dom";
import { DeviceType, TableauEventType, TableauViz, Toolbar } from "@tableau/embedding-api";

import { ColumnDef, flexRender, getCoreRowModel, getExpandedRowModel, Row as RowInfo, ColumnSort, useReactTable, getPaginationRowModel, ColumnFiltersState, getFilteredRowModel, SortingState, getSortedRowModel, ExpandedState, Updater, type Table as ITable } from "@tanstack/react-table";
import Auth from "../../common/auth/auth";
import { Defaults, Utils } from "@/common/utils";
import { isWithinRangeFilter, isActionPlanStatusFilter, isColumnNumberFilter } from "@/common/filters";
import { AppStore, AttachmentStore, CommentStore, FeatureGatingStore, MilestoneStore, ReportStore, SnapshotStore, StrategicPlanStore, TrustedTokenStore } from "@/stores";
import { type StrategicPlanItem } from "Stores/strategicPlanStore";
import { Dashboard } from "Stores/dashboardStore";
import { MilestoneStatus } from "Stores/milestoneStore";

import { Ticket, TicketStore } from "Stores/dashboardStore/tickets";

import { TicketDetailForm } from "./DetailForm";
import { D3Dashboard } from "./D3Actionplans";
import { SnapshotConfirmModal, ActionplanCloseModal } from "./ActionPlanModals";
import CONFIG from "Config";
import { useQuery } from "@/hooks/router";
import SearchActionPlans from "./SearchActionPlans";
import PagePagination from "./PagePagination";
import FilterButton from "./FilterMenu";
import { format } from "date-fns";
import TableMenu from "./TableMenu";
import { ArrowDown, ArrowDownUp, ArrowUp, EllipsisVertical, Eye, Plus, Upload } from "lucide-react";
import { ToastContext } from "@/contexts/toastContext";
import { useActionPlans } from "@/hooks/actionplans";

enum VIEW_OPTIONS {
    DEFAULT,
    STRATEGIC,
}

export interface ListProps {
    appStore?: AppStore;
    ticketStore?: TicketStore;
    reportStore?: ReportStore;
    strategicPlanStore?: StrategicPlanStore;
    trustedTokenStore?: TrustedTokenStore;
    dashboard?: Dashboard;
    instaOpenId?: string;
    overdue?: boolean;
    dashboardId?: number;
    refreshView?: string | null;
    history?: History;
    location?: Location;
    triggerSnapshot?: () => void;
    isReporting?: boolean;
    tickets?: Ticket[];
    selectTicket?: (row: RowInfo<Ticket>) => void;
    selectAllTickets?: (select: boolean, tickets: Ticket[] | undefined) => void;
    generateReport?: () => void;
    onNewSearch?: (fn: () => void) => void;
    pageSize?: number;
    isReadyToAddComment?: boolean;
    formRef?: any;
    hasToExpandActionPlan?: boolean;
    navigate?: NavigateFunction;
    featureGatingStore?: FeatureGatingStore;
    showQuickActionPlan?: boolean;
    attachmentStore?: AttachmentStore;
    snapshotStore?: SnapshotStore;
    milestoneStore?: MilestoneStore;
    commentStore?: CommentStore;
}

interface ListState {
    showFilters: boolean;
    showMore: boolean;
    instaOpenId: string;
    dashboardId?: number;
    fetchOnUpdateDashboard: boolean;
    pageSize: number;
    pageNumber: number;
    expand?: { [index: number]: boolean };
    initialLoadDone: boolean;
    didFetchAll: boolean;
    numOfTix: number;
    allTicketsSelected?: boolean;
    deletingActionPlan: undefined | string;
    showQuickActionPlan: boolean;
    view: VIEW_OPTIONS;
    refreshKey?: string | null;
    sortOptions: ColumnSort[];
    autoOpenIndex: number;
    openDropdownIndex: string | null;
    showSnapshotConfirm: boolean;
    showCloseModal: boolean;
    currentClosingTicket?: Ticket;
    isClosingActionPlan: boolean;
    closeError?: string;
    showActionPlanSuccess?: boolean;
}

const StatusMap = {
    open: "Open",
    closed: "Closed",
    "goal-met": "Achieved",
    "goal-not-met": "Unmet",
};

@inject("appStore", "ticketStore", "reportStore", "strategicPlanStore", "trustedTokenStore", "featureGatingStore", "attachmentStore", "snapshotStore", "milestoneStore", "commentStore")
@observer
export class TicketList extends Component<ListProps, ListState> {
    static contextType = ToastContext;
    declare context: React.ContextType<typeof ToastContext>;
    _isMounted = false;

    autoOpenIndex?: number;
    defaultPage = Defaults.tablePage();
    defaultPageSize = this.props.pageSize ? this.props.pageSize : Defaults.tablePageSize();
    columns: ColumnDef<Ticket>[];
    quickActionPlanRef;
    container: HTMLDivElement | null;
    auth?: Auth;
    listener: any;
    vizDidLoad: boolean;
    vizIsComplete: boolean;
    viz?: TableauViz;

    constructor(props: ListProps) {
        super(props);
        const params = new URLSearchParams(location.search);
        const pageNumber = params.get("page");
        const pageSize = params.get("page_size");
        this.state = {
            showMore: false,
            showFilters: true,
            expand: undefined,
            instaOpenId: this.props.instaOpenId ? this.props.instaOpenId : "",
            // get page number from query params or set defaults page
            pageNumber: pageNumber ? Number(pageNumber) : this.defaultPage,
            pageSize: pageSize ? Number(pageSize) : this.defaultPageSize,
            initialLoadDone: false,
            didFetchAll: false,
            numOfTix: this.props.tickets ? this.props.tickets.length : 0,
            allTicketsSelected: !!this.props.tickets,
            deletingActionPlan: undefined,
            showQuickActionPlan: !!props.showQuickActionPlan,
            view: VIEW_OPTIONS.DEFAULT,
            sortOptions: [],
            fetchOnUpdateDashboard: false,
            dashboardId: 0,
            refreshKey: this.props.refreshView,
            autoOpenIndex: -1,
            openDropdownIndex: null,
            showSnapshotConfirm: false,
            showCloseModal: false,
            currentClosingTicket: undefined,
            isClosingActionPlan: false,
            closeError: undefined,
            showActionPlanSuccess: false,
        };

        if (this.props.onNewSearch) {
            this.props.onNewSearch(this.resetPage);
        }

        const view = this.getParams().get("view");
        if (view && view.toUpperCase() === "SP") {
            this.state = { ...this.state, view: VIEW_OPTIONS.STRATEGIC, sortOptions: [{ id: "strategic_plan_name", desc: false }] };
        }

        this.columns = this.getColumns();
    }

    filterableColumns = [
        { key: "assignee_name", label: "Assignee" },
        { key: "created_by_name", label: "Created By" },
        { key: "dashboard", label: "Dashboard name" },
        { key: "name", label: "Observation" },
        { key: "collaborator_name", label: "Collaborator Name" },
        { key: "student", label: "Student Name" },
        { key: "course", label: "Course Name" },
    ];

    params = new URLSearchParams(window.location.search);

    tableRef = createRef<ActionPlanTableHandle>();

    toggleDropdown = (rowIndex: string) => {
        this.setState((prevState) => ({
            openDropdownIndex: prevState.openDropdownIndex === rowIndex ? null : rowIndex,
        }));
    };

    ensureAuth() {
        if (this.vizIsComplete && !this.vizDidLoad && this.auth) {
            this.auth.loginRedirect();
        }
    }

    handleVizLoaded = () => {
        this.vizDidLoad = true;
    };

    static getDerivedStateFromProps(nextProps: ListProps, state) {
        let nextState: Partial<ListState> = {};
        if (!state || (nextProps.instaOpenId && nextProps.instaOpenId !== state.instaOpenId)) {
            nextState = { instaOpenId: nextProps.instaOpenId };
        }

        if (!state || (nextProps.dashboardId && nextProps.dashboardId !== state.dashboardId)) {
            nextState = { ...nextState, dashboardId: nextProps.dashboardId };
            if (state && state.initialLoadDone) {
                nextState = { ...nextState, fetchOnUpdateDashboard: true, initialLoadDone: false, didFetchAll: false, expand: {} };
            }
        }

        if (Object.keys(nextState).length !== 0) {
            return nextState;
        }

        return null;
    }

    componentDidMount() {
        if (!this.props.dashboardId) {
            const listener = (msg: any) => {
                if (msg.data && _.isString(msg.data) && msg.data.includes("tableau.completed")) {
                    this.vizIsComplete = true;
                    setTimeout(() => this.ensureAuth(), 2000);
                }
            };
            this.listener = listener;
            this.auth = new Auth();
            addEventListener("message", listener);
        }

        if (!this.props.isReporting && !this.props.dashboardId) {
            Utils.setViewName("TicketList", "ActionPlans®");
        }

        if (this.props.tickets) {
            this.setState({ initialLoadDone: true, didFetchAll: false });
            return;
        }
        this.fetchAppropriateTickets(undefined, true);

        this._isMounted = true;
        if (this.props.strategicPlanStore) this.props.strategicPlanStore.fetchStrategicPlanItems(true);
        this.checkRenderAndInit();
        if (this.props.showQuickActionPlan) {
            setTimeout(() => {
                if (this.quickActionPlanRef !== undefined) {
                    this.quickActionPlanRef.scrollIntoView();
                }
            }, 1000);
        }
    }

    componentWillUnmount() {
        delete this.auth;
        this._isMounted = false;
        this.setState({ initialLoadDone: false, didFetchAll: false });
        if (this.viz) {
            this.viz.remove();
            this.viz.removeEventListener(TableauEventType.FirstInteractive, () => {});
            this.viz = undefined;
        }
    }

    async componentDidUpdate() {
        if (this.state.fetchOnUpdateDashboard) {
            this.setState({ fetchOnUpdateDashboard: false });
            if (this.state.dashboardId) {
                await this.fetchAppropriateTickets(this.state.dashboardId, true);
            }
        }
    }

    checkRenderAndInit = () => {
        if (!this.container) {
            setTimeout(this.checkRenderAndInit, 500);
        } else {
            this.initTableau();
        }
    };

    handleActionplanReload = () => {
        if (this.props.dashboardId) {
            this.props.ticketStore!.getTicketsForDashboard(this.props.dashboardId, this.state.pageSize, 0).then(() => {
                this.setState({ expand: this.getInitialExpandedRow() });
            });
        } else {
            this.props.ticketStore!.fetchTickets(this.state.view === VIEW_OPTIONS.STRATEGIC).then(() => {
                this.setState({ expand: this.getInitialExpandedRow(), numOfTix: this.props.ticketStore!.tickets ? this.props.ticketStore!.tickets.length : 0 });
            });
        }
    };

    handleFiltersChange = (numOfTix: number) => {
        this.setState({ expand: undefined, pageNumber: this.defaultPage, numOfTix });
    };

    pushHistory = (url, params?) => {
        if (!this.props.navigate) return;
        this.props.navigate(`${url}?${params ? params : this.getParams()}`);
    };

    getParams = () => {
        const location = this.props.location;
        return new URLSearchParams(location ? location.search : location);
    };

    handleSelectAllToggle = () => {
        if (this.props.selectAllTickets) {
            const tix = this.props.tickets || !this.props.ticketStore ? [] : this.props.ticketStore.tickets;
            const shouldSelectAll = this.props.tickets ? false : this.props.ticketStore && this.props.ticketStore.allSelected !== undefined ? !this.props.ticketStore.allSelected : !this.state.allTicketsSelected;
            this.props.selectAllTickets(shouldSelectAll, tix);
        }
        this.setState({ allTicketsSelected: !this.state.allTicketsSelected });
        // Recalculate this.columns to force a rerender of react-table to update the header checkbox
        this.columns = this.getColumns();
    };

    getStrategicPlanItems = (items: StrategicPlanItem[]) => items.map((item) => item.name).join(", ");

    isActionPlanSelected = (row: Ticket) => {
        if (this.props.reportStore?.selectedTickets) {
            return this.props.reportStore.selectedTickets.find(ticket => ticket.ruuid === row.ruuid) !== undefined;
        }
        return false;
    };

    getColumns = () => {
        const columns: ColumnDef<Ticket>[] = [];

        columns.push(
            {
                header: "ID",
                accessorKey: "display_id",
                size: 50,
                filterFn: isColumnNumberFilter,
            },
            {
                header: "Created on",
                accessorKey: "created",
                cell: (props) => format(new Date(props.row.original.created), "MMM d, yyyy"),
                filterFn: isWithinRangeFilter,
                size: 150,
            },
            {
                header: "Due Date",
                accessorKey: "evaluation_date",
                filterFn: isWithinRangeFilter,
                cell: (props) => {
                    if (props.row.original.type === "observation" && props.row.original.evaluation_date === "") return "N/A";
                    if (props.row.original.evaluation_date === "") return "";
                    if (typeof props.row.original.evaluation_date !== "string") return "";
                    return format(new Date(props.row.original.evaluation_date), "MMM d, yyyy");
                },
                size: 100,
            },
            {
                header: "Status",
                accessorKey: "status",
                cell: (props) => {
                    if (props.row.original.type === "observation") return "N/A";
                    const isTargetMet = props.row.original.target_met;
                    // const statusKey = !!props.row.original.target_met ? " goal-met" : " goal-not-met";
                    const status = props.row.original.status;
                    if (!status || typeof status !== "string") {
                        return "";
                    }

                    return (
                        <div className="badge-container">
                            <Badge bg="light" className={`status-${isTargetMet ? "goal-met" : status.toLocaleLowerCase()}`}>
                                {status ? StatusMap[isTargetMet ? "goal-met" : status] : "N/A"}
                            </Badge>
                        </div>
                    );
                },
                filterFn: isActionPlanStatusFilter,
                minSize: 95,
                maxSize: 100,
            }
        );

        if (this.state.view === VIEW_OPTIONS.STRATEGIC) {
            columns.push({
                id: "strategic_plan_items",
                header: "Institutional Effectiveness Plan",
                accessorKey: "strategic_plan_items",
                cell: ({ row }) => {
                    return row.original.strategic_plan_items.length ? this.getStrategicPlanItems(row.original.strategic_plan_items) : "-";
                },
                size: 200,
            });
        } else {
            columns.push({
                header: "Dashboard Name",
                accessorKey: "dashboard",
                cell: (props) => Utils.getDisplayDashboardName(props.row.original.dashboard),
                size: 150,
            });
        }

        columns.push(
            {
                header: "Observation",
                accessorKey: "name",
                size: 350,
            },
            {
                header: "Author",
                accessorKey: "created_by_name",
                cell: (props) => {
                    return props.row.original.created_by_name ? props.row.original.created_by_name : "";
                },
                size: 125,
            },
            {
                header: "Assignee",
                id: "assignee_name",
                accessorKey: "assignee_name",
                cell: (props) => {
                    return props.row.original.assignee_name ? props.row.original.assignee_name : "";
                },
                size: 125,
            }
        );

        if (!this.props.isReporting) {
            // edit button only if regular (not reporting) view
            columns.push({
                header: () => null,
                accessorKey: "expand_plan",
                minSize: 50,
                maxSize: 50,
                enableColumnFilter: false,
                cell: (props) => {
                    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        event.preventDefault();
                        props.row.getToggleExpandedHandler()();
                    };

                    return (
                        <button className="btn btn-icon btn-toggle-expand" onClick={handleClick} aria-label="Expand or Compress">
                            <i className={`icon icon-${props.row.getIsExpanded() ? "compress" : "expand"}`} />
                        </button>
                    );
                },
            });
        } else {
            // add checkboxes if report choice view
            columns.unshift({
                header: () => (
                    <Button variant={"icon"} onClick={this.handleSelectAllToggle} disabled={this.props.tickets && this.props.tickets.length === 0}>
                        {this.props.ticketStore!.allSelected || this.props.tickets ? <i className="icon icon-check-square" /> : <i className="icon icon-square-o" />}
                    </Button>
                ),
                accessorKey: "isSelected",
                cell: (props) => (
                    <Button
                        variant={"icon"}
                        onClick={(ev) => {
                            ev.stopPropagation();
                            if (this.props.selectTicket) {
                                this.props.selectTicket(props.row);
                            }
                            this.columns = this.getColumns();
                        }}
                    >
                        {this.isActionPlanSelected(props.row.original) ? <i className="icon icon-check-square" /> : <i className="icon icon-square-o" />}
                    </Button>
                ),
                minSize: 40,
                maxSize: 50,
                enableColumnFilter: false,
                enableSorting: false,
            });
        }

        columns.push({
            header: () => null,
            accessorKey: "actions",
            size: 50,
            enableColumnFilter: false,
            cell: (props) => {
                const rowId = props.row.id;
                const isOpen = this.state.openDropdownIndex === rowId;

                return (
                    <div className="d-flex align-items-center gap-1">
                        <TableMenu
                            key={props.row.original.display_id}
                            dashboardId={this.state.dashboardId}
                            row={props.row} isOpen={isOpen}
                            toggleDropdown={this.toggleDropdown}
                            onDeletePlan={this.handleDeleteClick}
                            onDownloadPlan={this.savePDF}
                            onToggleStatus={this.handleToggleStatus}
                        />
                        {this.props.ticketStore!.fetchingPDF === props.row.original.ruuid ? <Spinner animation="border" size="sm" /> : null}
                    </div>
                );
            },
        });

        return columns;
    };

    getInitialExpandedRowIndex = (actionPlans?: Ticket[]) => {
        let idx;
        if (!this.props.ticketStore || !this.state.instaOpenId) return;
        if (this.state.instaOpenId) {
            // idx = _.findIndex(this.props.ticketStore.tickets, { id: this.props.instaOpenId })
            // the only reason for using this less readable version of findIndex is that TS doesn't understand PartialDeep
            // We are only checking for the deprecated (old hashed id) here while we support old links from notifications.
            const tickets = actionPlans ? actionPlans : this.props.ticketStore.tickets;
            idx = _.findIndex(tickets, (o) => {
                return o.ruuid === this.state.instaOpenId;
            });
        }
        return idx;
    };

    getInitialExpandedRow = (actionPlans?: Ticket[]) => {
        const idx = this.getInitialExpandedRowIndex(actionPlans);
        if (idx !== undefined) {
            const visibleIdx = idx >= this.defaultPageSize ? idx % this.defaultPageSize : idx;
            const open: { [index: number]: boolean } = { [visibleIdx]: true };
            return open;
        } else {
            return undefined;
        }
    };

    getInitialPageNumber = (actionPlans?: Ticket[]) => {
        const idx = this.getInitialExpandedRowIndex(actionPlans);
        if (idx && idx >= this.defaultPageSize) {
            return Math.floor(idx / this.defaultPageSize);
        } else {
            return this.defaultPage;
        }
    };

    resetPage = () => {
        // try to be a little smart about it to avoid unnecessary diffing
        if (this.state.pageNumber !== this.defaultPage) {
            this.setState({ pageNumber: this.defaultPage });
        }
    };

    handleScrollTo = () => {
        if (this.props.instaOpenId) {
            // this is the first time the page is loaded
            // and there is a task to automatically expand
            // We need to find that element and scroll it into view
            const idx = this.getInitialExpandedRowIndex();
            if (idx === undefined) return;
            const visibleIdx = idx > this.defaultPageSize ? idx % this.defaultPageSize : idx;
            const listItem = document.querySelector(`div.list-container tbody tr:nth-child(${visibleIdx + 1})`);
            if (listItem) {
                listItem.scrollIntoView();
            }
        }
    };

    pushHistoryOnExpand = (ticketId?: string) => {
        this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name, ticketId));
    };

    listName = () => {
        const filters = this.state.showFilters ? " show-filters" : "";
        const fixed = this.props.dashboard || (this.props.isReporting && this.props.tickets) ? " not-fixed" : "";
        const reporting = this.props.isReporting ? " reporting" : "";
        const selection = this.props.isReporting && this.props.tickets ? " selection" : "";
        // reporting is fine without this part

        return `list-container${filters}${fixed}${reporting}${selection}`;
    };

    savePDF = (ticketId: string) => {
        this.props.ticketStore!.getTicketPDF(ticketId);
    };

    deleteActionPlan = (id: string) => {
        const resetDeleteState = () => {
            this.setState({ deletingActionPlan: undefined });
        };
        this.setState({ deletingActionPlan: id, expand: undefined }, () => {
            this.props.ticketStore!.deleteTicket(id).then(() => {
                if (this.props.dashboardId) {
                    this.props.ticketStore!.getTicketsForDashboard(this.props.dashboardId, this.state.pageSize, this.state.pageNumber).then(resetDeleteState);
                } else {
                    this.props.ticketStore!.fetchTickets(this.state.view === VIEW_OPTIONS.STRATEGIC, this.state.pageSize, this.state.pageNumber).then(resetDeleteState);
                }
            });
        });
    };

    handleDeleteClick = (ticket: Ticket) => {
        this.props.appStore!.showConfirmModal({
            element: <span>{`Are you sure you want to delete ActionPlan ${ticket.display_id}?`}</span>,
            accept: () => this.deleteActionPlan(ticket.ruuid),
            showActions: true,
        });
    };

    handleToggleStatus = (ticket: Ticket) => {
        if (ticket.type !== "task") return;

        if (ticket.status === "open") {
            this.checkMilestonesAndProceed(ticket);
        } else {
            this.props.appStore!.showConfirmModal({
                element: <div>Are you sure you want to re-open this ActionPlan?</div>,
                accept: () => {
                    this.reopenActionPlan(ticket);
                },
                cancel: () => {},
                showActions: true,
            });
        }
    };

    checkMilestonesAndProceed = async (ticket: Ticket) => {
        try {
            if (this.props.milestoneStore) {
                await this.props.milestoneStore.getAllMilestonesForActionPlan(ticket.ruuid);

                const hasIncompleteMilestones = this.props.milestoneStore.milestones.some(
                    milestone => milestone.status !== MilestoneStatus.COMPLETE
                );

                if (hasIncompleteMilestones) {
                    this.props.appStore!.showConfirmModal({
                        element: <p>This ActionPlan has milestones that have not been completed. Do you want to continue or do you want to cancel closing the ActionPlan for now?</p>,
                        accept: () => {
                            this.proceedWithClose(ticket);
                        },
                        cancel: () => {},
                        showActions: true,
                    });
                } else {
                    this.proceedWithClose(ticket);
                }
            } else {
                this.proceedWithClose(ticket);
            }
        } catch (error) {
            console.error("Error checking milestones:", error);
            this.proceedWithClose(ticket);
        }
    };

    proceedWithClose = (ticket: Ticket) => {
        this.setState({
            currentClosingTicket: ticket,
            showSnapshotConfirm: true,
        });
    };

    handleCloseWithSnapshot = () => {
        const ticket = this.state.currentClosingTicket!;
        this.setState({
            showSnapshotConfirm: false,
            currentClosingTicket: undefined
        });

        if (this.props.navigate && ticket.dashboard_id) {
            const dashboardUrl = Utils.getActionplanUrl(ticket.dashboard, ticket.ruuid);
            this.props.navigate(`${dashboardUrl}?closeActionPlan=${ticket.ruuid}`);
        }
    };

    handleCloseWithoutSnapshot = () => {
        this.setState({
            showSnapshotConfirm: false,
            showCloseModal: true,
        });
    };

    handleCancelClose = () => {
        this.setState({
            showSnapshotConfirm: false,
            showCloseModal: false,
            currentClosingTicket: undefined,
            closeError: undefined,
        });
    };

    handleSaveClose = async (finalComment: string, goalMet: boolean, attachmentId?: string) => {
        const ticket = this.state.currentClosingTicket!;
        this.setState({ isClosingActionPlan: true, closeError: undefined });

        try {
            const actionplanData: any = {
                ruuid: ticket.ruuid,
                status: "closed",
                target_met: goalMet,
                dashboard_id: ticket.dashboard_id,
            };

            const commentData: any = {
                comment: finalComment,
                created_by: Utils.getCurrentUser()?.name || undefined,
                attachments: [],
            };

            const skipSnapshotGeneration = false;
            let closingAttachmentId: string | undefined = undefined;

            if (attachmentId) {
                commentData.attachments.push(attachmentId);
                closingAttachmentId = attachmentId;
            }

            actionplanData.closed_attachment_id = closingAttachmentId;
            commentData.closed_attachment_id = closingAttachmentId;

            await this.props.ticketStore!.createOrUpdateTicket(actionplanData, Utils.getChosenSchoolInfo());

            if (this.props.commentStore) {
                await this.props.ticketStore!.addCommentToTicket(ticket.ruuid, commentData, skipSnapshotGeneration);
            }

            this.handleActionplanReload();

            this.setState({
                showCloseModal: false,
                currentClosingTicket: undefined,
                isClosingActionPlan: false,
            });

        } catch (error) {
            console.error("Error closing ActionPlan:", error);
            this.setState({
                closeError: "Failed to close ActionPlan. Please try again.",
                isClosingActionPlan: false,
            });
        }
    };

    reopenActionPlan = async (ticket: Ticket) => {
        try {
            const actionplanData = {
                ruuid: ticket.ruuid,
                status: "open",
                dashboard_id: ticket.dashboard_id,
                closed_dashboard_image_url: "",
                snapshot_image_url: null,
            };

            await this.props.ticketStore!.createOrUpdateTicket(actionplanData, Utils.getChosenSchoolInfo());
            this.handleActionplanReload();
        } catch (error) {
            console.error("Error reopening ActionPlan:", error);
            this.props.appStore!.showConfirmModal({
                element: <div>Failed to re-open ActionPlan. Please try again.</div>,
                accept: () => {},
                showActions: true,
            });
        }
    };

    handleClearSearch = () => {
        this.props.selectAllTickets ? this.props.selectAllTickets(false, this.props.tickets || !this.props.ticketStore ? [] : this.props.ticketStore.tickets) : null;
    };

    initTableau = async () => {
        if (this.viz) {
            this.viz.remove();
            this.viz = undefined;
        }

        if (this.props.trustedTokenStore) {
            await this.props.trustedTokenStore.getTrustedToken();
        }

        const chosenSchool = Utils.getChosenSchool();
        if (!chosenSchool) return;
        const vizUrl = CONFIG.paths.actionPlanDashboard.replace(":schoolId", chosenSchool);

        this.viz = new TableauViz();
        this.viz.src = vizUrl;
        this.viz.hideTabs = true;
        this.viz.toolbar = Toolbar.Hidden;
        this.viz.device = DeviceType.Desktop;
        this.viz.addEventListener(TableauEventType.FirstInteractive, async () => {
            await this.handleVizLoaded();
        });
    };

    handleToggleView = () => {
        const newView = this.state.view === VIEW_OPTIONS.DEFAULT ? VIEW_OPTIONS.STRATEGIC : VIEW_OPTIONS.DEFAULT;
        this.setState({ view: newView, sortOptions: newView === VIEW_OPTIONS.DEFAULT ? [] : [{ id: "strategic_plan_name", desc: false }] }, () => {
            this.handleActionplanReload();
            this.columns = this.getColumns();

            const params = this.getParams();
            if (newView === VIEW_OPTIONS.STRATEGIC) {
                params.set("view", "sp");
                this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name), params);
            } else {
                params.delete("view");
                this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name), params);
            }
        });
    };

    handleGenerateReport = () => {
        this.props.generateReport ? this.props.generateReport() : null;
    };

    handleCreateReportClick = () => {};

    handleShowQuickActionPlan = () => {
        this.setState({ showQuickActionPlan: true }, () => {
            if (this.quickActionPlanRef !== undefined) {
                this.quickActionPlanRef.scrollIntoView();
            }
        });
    };

    getQuickActionPlanRef = (ref) => {
        this.quickActionPlanRef = ref;
    };

    getPageRangeElement = (from: any, to: any, max: any) => (
        <div className="range">
            Showing{" "}
            <span className="fw-bold">
                {from} - {to}
            </span>{" "}
            out of <span className="fw-bold">{max}</span>
        </div>
    );

    shouldShowD3 = () => {
        return !this.props.dashboard && !this.props.isReporting;
    };

    renderPageRange = () => {
        const currentPageMin = this.state.pageSize * (this.state.pageNumber + 1) - this.state.pageSize + 1;
        const currentPageMax = this.state.pageSize * (this.state.pageNumber + 1);

        let to: any;
        let max: any;

        if (this.props.isReporting) {
            if (!this.props.tickets && this.props.ticketStore!.tickets) {
                // Report search results actionplan list
                to = Math.min(currentPageMax, this.props.ticketStore!.tickets.length);
                max = this.props.ticketStore!.tickets.length;
            } else {
                // Report selected actionplans list
                // if there are no selected Actionplans, render nothing
                if (!this.props.reportStore!.selectedTickets) return null;
                to = Math.min(currentPageMax, this.props.reportStore!.selectedTickets.length);
                max = this.props.reportStore!.selectedTickets.length;
            }
        } else {
            // All ActionPlans list (/actionplans or /dashboard/some_id)
            to = Math.min(currentPageMax, this.state.numOfTix);
            max = this.state.numOfTix;
        }

        return this.getPageRangeElement(currentPageMin, to, max);
    };

    updateCurrentPage = (page: number) => {
        this.setState({ pageNumber: page });
    };

    updatePageSize = (pageSize: number) => {
        this.setState({ pageSize: pageSize });
    };

    renderDecisionsList = () => {
        if (!this.props.ticketStore || !this.props.ticketStore.tickets || !this.props.strategicPlanStore || !this.props.strategicPlanStore.strategicPlanItems) return;

        const className = this.props.dashboard ? "dashboard-list-action-bar" : "";

        const titleText = this.state.view === VIEW_OPTIONS.STRATEGIC ? "Institutional Effectiveness" : "";
        const viewText = this.state.view === VIEW_OPTIONS.STRATEGIC ? "All" : "Institutional Effectiveness";
        return (
            <React.Fragment>
                {!this.props.isReporting && !this.props.dashboard ? (
                    <Row className={`section-action-bar actionplans ${className}`}>
                        <Col md={12} lg={4} className="title">
                            <h3 id="actionPlansTitle">{titleText} ActionPlans</h3>
                        </Col>
                        <Col>
                            <div className="actions">
                                {!this.props.dashboard && (this.state.view === VIEW_OPTIONS.STRATEGIC || this.props.strategicPlanStore.strategicPlanItems.length > 0) ? (
                                    <Dropdown className="ap-show-more" show={this.state.showMore} onToggle={(isOpen) => this.setState({ showMore: isOpen })}>
                                        <Dropdown.Toggle data-test-id="ap-show-more-btn" id="showMore" variant="outline" className={`expnad-menu ${this.state.showMore ? "active" : ""}`}>
                                            <span className="d-flex align-items-center gap-2">
                                                <EllipsisVertical />
                                                More
                                            </span>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            <Dropdown.Item className="d-flex align-items-center gap-2" onClick={this.handleToggleView}>
                                                <Eye />
                                                <span className="fw-normal">View {viewText} ActionPlans</span>
                                            </Dropdown.Item>
                                            {this.props.featureGatingStore && !this.props.dashboard ? (
                                                Utils.isFeatureActive("api", "data_export", this.props.featureGatingStore.featureGatingList) ? (
                                                    <Dropdown.Item className="d-flex align-items-center gap-2" onClick={() => this.exportAllActionPlans()}>
                                                        <Upload />
                                                        <span className="fw-normal">Export All ActionPlan</span>
                                                    </Dropdown.Item>
                                                ) : null
                                            ) : null}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ) : null}
                                {this.props.dashboard || !Utils.hasAdminAccess() ? null : (
                                    <NavLink id="report-create-link" to={`${Utils.getLinkBase()}/reports/create`} title="New Report" aria-label="New Report" onClick={this.handleCreateReportClick}>
                                        {" "}
                                        <Plus /> New Report
                                    </NavLink>
                                )}
                                {this.props.dashboard ? null : (
                                    <button id="create-actionplan" title="Create ActionPlan" aria-label="Create ActionPlan" onClick={this.handleShowQuickActionPlan}>
                                        <Plus />
                                        New ActionPlan
                                    </button>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : null}
                {this.shouldShowD3() ? <D3Dashboard overdue={this.props.overdue} /> : null}
                <div className={this.listName()}>
                    <div className="action-container">
                        {this.props.isReporting ? (
                            <div className="component-actions">
                                <Row>
                                    <Col xs={12} md={6}>
                                        <span>{this.props.tickets ? "selected for report" : "search results"}</span>
                                    </Col>
                                    {this.props.tickets ? (
                                        <Col>
                                            <div className="ticket-selector">
                                                <Button variant="secondary" size="lg" disabled={this.props.selectAllTickets === undefined || this.props.tickets.length === 0} onClick={this.handleClearSearch}>
                                                    Clear Selected Search Results
                                                </Button>
                                                <Button variant="secondary" size="lg" onClick={this.handleGenerateReport} disabled={this.props.tickets.length === 0}>
                                                    Generate Report
                                                </Button>
                                            </div>
                                        </Col>
                                    ) : null}
                                </Row>
                            </div>
                        ) : null}
                        {this.state.showQuickActionPlan ? (
                            <>
                                <TicketDetailForm formRef={this.getQuickActionPlanRef} isPreview={false} dashboard={undefined} onCancel={this.handleCancel} onSaveDone={this.handleSaveDone} />
                            </>
                        ) : null}
                        <div className="list-actions border-top border-bottom">
                            <Row className="actionplan-actions align-items-center">
                                <Col xs={12} md={6} className="d-flex justify-content-start">
                                    {this.renderPageRange()}
                                </Col>
                                <Col>
                                    <div className="d-flex justify-content-end gap-2">
                                        <SearchActionPlans
                                            urlParams={this.params}
                                            columns={this.filterableColumns}
                                            onSearch={(search, key) => {
                                                const table = this.tableRef.current?.getTableInstance();

                                                if (!search || search.length === 0) {
                                                    // Only clear the specific search column, preserve other filters
                                                    if (table?.getColumn(key)) {
                                                        table?.getColumn(key)?.setFilterValue(undefined);
                                                    }
                                                } else {
                                                    if (table?.getColumn(key)) {
                                                        table?.getColumn(key)?.setFilterValue(search);
                                                    }
                                                }
                                            }}
                                        />

                                        <FilterButton
                                            urlParams={this.params}
                                            onApplyFilter={(filters) =>
                                                Object.entries(filters).forEach(([key, value]) => {
                                                    const table = this.tableRef.current?.getTableInstance();

                                                    if (table?.getColumn(key) && value) {
                                                        table?.getColumn(key)?.setFilterValue(value);
                                                    }
                                                })
                                            }
                                            onResetFilter={() => {
                                                const table = this.tableRef.current?.getTableInstance();
                                                table?.resetColumnFilters();
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                    <ActionPlanReactTable
                        ref={this.tableRef}
                        columns={this.columns}
                        data={this.props.tickets}
                        view={this.state.view}
                        dashboard={this.props.dashboard}
                        isReporting={this.props.isReporting}
                        pageIndex={this.state.pageNumber}
                        pageSize={this.state.pageSize}
                        showFilters={this.state.showFilters}
                        numOfTickets={this.state.numOfTix}
                        refreshView={this.state.refreshKey}
                        autoOpenIndex={this.state.autoOpenIndex}
                        handleActionplanReload={this.handleActionplanReload}
                        handleFiltersChange={this.handleFiltersChange}
                        refreshSelectedActionPlan={this.refreshSelectedActionPlan}
                        handleCancel={this.handleCancel}
                        handleSaveDone={this.handleSaveDone}
                        handleCloseDone={this.handleCloseDone}
                        pushHistory={this.pushHistoryOnExpand}
                        updateCurrentPage={this.updateCurrentPage}
                        updatePageSize={this.updatePageSize}
                    />
                </div>
            </React.Fragment>
        );
    };
    shouldFetchAll = () => {
        return this.state.initialLoadDone && !this.state.didFetchAll && this.props.ticketStore && this.props.ticketStore.tickets && this.props.ticketStore.tickets.length <= this.state.pageSize;
    };

    fetchAppropriateTickets = async (id?: number, isFirstLoadOnDashboard = false) => {
        if (this.props.ticketStore) {
            this.setState({ expand: {} });

            if (id || this.props.dashboardId) {
                const dashId = id || this.props.dashboardId;
                if (!dashId) return;

                if (this.shouldFetchAll()) {
                    await this.props.ticketStore.getTicketsForDashboard(dashId, undefined, undefined, isFirstLoadOnDashboard);
                    if (this._isMounted) this.setState({ didFetchAll: true });
                } else if (!this.state.didFetchAll) {
                    await this.props.ticketStore.getTicketsForDashboard(dashId, this.state.pageSize, this.props.instaOpenId ? this.getInitialPageNumber() : this.state.pageNumber, isFirstLoadOnDashboard);
                }
            } else {
                if (this.shouldFetchAll()) {
                    await this.props.ticketStore.fetchTickets(this.state.view === VIEW_OPTIONS.STRATEGIC);
                    if (this._isMounted) this.setState({ didFetchAll: true });
                } else if (!this.state.didFetchAll) {
                    await this.props.ticketStore.fetchTickets(this.state.view === VIEW_OPTIONS.STRATEGIC, this.state.pageSize, this.state.pageNumber);
                }
            }

            if (this.props.instaOpenId && this.props.ticketStore.tickets) {
                this.handleAutoOpen();
            }

            if (this.props.ticketStore.tickets) {
                if (this._isMounted) {
                    this.setState(
                        {
                            initialLoadDone: true,
                            numOfTix: this.props.ticketStore.tickets.length,
                        },
                        () => {
                            if (this.state.numOfTix <= this.state.pageSize && !this.state.didFetchAll) {
                                this.fetchAppropriateTickets(id || this.props.dashboardId);
                            }
                        }
                    );
                }
            }
        }
    };

    handleAutoOpen = () => {
        this.autoOpenIndex = this.getInitialExpandedRowIndex();
        if (this._isMounted) {
            this.setState(
                {
                    expand: this.getInitialExpandedRow(),
                    pageNumber: this.getInitialPageNumber(),
                    autoOpenIndex: this.autoOpenIndex ?? -1,
                },
                () => {
                    this.handleScrollTo();
                }
            );
        }
    };
    exportAllActionPlans = () => {
        this.props.ticketStore?.exportAllActionPlans();
    };

    refreshSelectedActionPlan = (actionPlans: Ticket[]) => {
        this.autoOpenIndex = this.getInitialExpandedRowIndex();
        if (this._isMounted) {
            this.setState({
                expand: this.getInitialExpandedRow(),
                pageNumber: this.getInitialPageNumber(actionPlans),
                autoOpenIndex: this.autoOpenIndex ?? -1,
            });
        }
    };

    render() {
        return (
            <div ref={this.props.formRef}>
                {this.props.ticketStore && this.props.ticketStore.tickets && this.state.initialLoadDone ? this.renderDecisionsList() : null}
                {this.props.ticketStore && (!this.props.ticketStore.tickets || this.props.ticketStore.tickets.length !== 0) && !this.state.initialLoadDone ? (
                    <div style={{ textAlign: "center", paddingTop: 180 }}>
                        <Spinner animation="border" />
                    </div>
                ) : null}

                <SnapshotConfirmModal
                    show={this.state.showSnapshotConfirm}
                    onCancel={this.handleCancelClose}
                    onWithSnapshot={this.handleCloseWithSnapshot}
                    onWithoutSnapshot={this.handleCloseWithoutSnapshot}
                />

                <ActionplanCloseModal
                    show={this.state.showCloseModal}
                    onCancel={this.handleCancelClose}
                    onSave={this.handleSaveClose}
                    isQuickActionplan={!this.state.currentClosingTicket?.dashboard_id}
                    isSaving={this.state.isClosingActionPlan}
                />

                {this.state.closeError && (
                    <Modal show={true} onHide={() => this.setState({ closeError: undefined })}>
                        <Modal.Body>
                            <div className="alert alert-danger">
                                {this.state.closeError}
                            </div>
                            <div className="text-end">
                                <Button onClick={() => this.setState({ closeError: undefined })}>
                                    OK
                                </Button>
                            </div>
                        </Modal.Body>
                    </Modal>
                )}
            </div>
        );
    }

    handleSaveDone = async (ruuid?: string) => {
        // Show success, keep form open, and expand the just-created action plan
        if (this.context && this.context.toast) {
            this.context.toast({
                type: "success",
                title: "Success",
                message: "Action Plan created successfully. You can continue editing or close this form.",
                duration: 5000,
            });
        }
        this.setState({ showQuickActionPlan: true, showActionPlanSuccess: true, instaOpenId: ruuid || "" }, async () => {
            await this.fetchAppropriateTickets();
            this.handleAutoOpen();
        });
    };

    handleCloseDone = () => {
        if (this.props.instaOpenId && this.props.ticketStore && this.props.ticketStore.tickets) {
            this.handleAutoOpen();
        }
    };

    handleCancel = () => {
        this.autoClose();
    };

    autoClose = (nextState?: any, callback?: any) => {
        if (this.props.dashboardId) {
            const params = this.getParams();
            params.delete("viewId");
            params.delete("refreshView");
            this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name), params);
        } else if (!this.props.isReporting) {
            this.pushHistory(Utils.getActionplanUrl());
        }

        this.setState({ instaOpenId: "", autoOpenIndex: -1, expand: {}, showQuickActionPlan: false, ...nextState }, callback || (() => {}));
    };
}

interface ActionPlanReactTableProps {
    columns: ColumnDef<Ticket>[];
    data?: Ticket[];
    dashboard?: Dashboard;
    isReporting?: boolean;
    pageIndex: number;
    pageSize: number;
    showFilters: boolean;
    numOfTickets: number;
    autoOpenIndex: number;
    refreshView?: string | null;
    handleActionplanReload: () => void;
    handleFiltersChange: (rows: number) => void;
    refreshSelectedActionPlan: (actionPlans: Ticket[]) => void;
    handleCancel: () => void;
    handleSaveDone: () => void;
    handleCloseDone: () => void;
    pushHistory: (ticketId?: string) => void;
    updateCurrentPage: (pg: number) => void;
    updatePageSize: (pgSize: number) => void;
    view: VIEW_OPTIONS;
}

export type ActionPlanTableHandle = {
    getTableInstance: () => ITable<Ticket>;
};

const ActionPlanReactTable = forwardRef<ActionPlanTableHandle, ActionPlanReactTableProps>(({ columns, data, view, dashboard, isReporting, pageIndex, pageSize,
                                                                                               numOfTickets, autoOpenIndex, refreshView, handleActionplanReload, handleFiltersChange, refreshSelectedActionPlan, handleCancel, handleSaveDone, handleCloseDone, pushHistory, updateCurrentPage, updatePageSize }, ref) => {
    const query = useQuery();
    const navigate = useNavigate();
    const location = useLocation();

    // Add URL parameter reading for all filters
    const statusFilter = query.get("status");
    const createdStartFilter = query.get("created_start");
    const createdEndFilter = query.get("created_end");
    const evaluationStartFilter = query.get("evaluation_start");
    const evaluationEndFilter = query.get("evaluation_end");
    const queryFilter = query.get("query");
    const queryByFilter = query.get("key");
    const sortFilter = query.get("sort");
    const sortOrderFilter = query.get("order");
    const pageFilter = query.get("page");
    const pageSizeFilter = query.get("page_size");

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const pagination = useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [initialExpander, setInitialExpander] = useState(true);
    const {data: actionplansData} = useActionPlans({
        strategicPlanOnly: view === VIEW_OPTIONS.STRATEGIC,
        pageSize: pageSizeFilter ? parseInt(pageSizeFilter) : pageSize,
        pageNumber: pageFilter ? parseInt(pageFilter) - 1 : pageIndex,
        queryFilter,
        queryByFilter,
        statusFilter,
        sortFilter,
        sortOrderFilter,
        createdStartFilter,
        createdEndFilter,
        evaluationStartFilter,
        evaluationEndFilter
    }, !data);

    const isExpandState = (updater: Updater<ExpandedState>): updater is ExpandedState => {
        const castUpdater = updater as ExpandedState;

        return typeof castUpdater === "boolean" || typeof castUpdater === "object";
    };

    const totalPages = Math.ceil((numOfTickets ?? 0) / (pageSize || 10));

    const setExpandHandler = (updater: Updater<ExpandedState>) => {
        if (isExpandState(updater)) {
            setExpanded(updater);
        } else {
            const news = updater(expanded);
            const oldKeys = Object.keys(expanded);
            const newKeys = Object.keys(news);
            const filteredKeys = newKeys.filter((key) => !oldKeys.includes(key));
            const newExpanded: Record<string, boolean> = {};
            filteredKeys.forEach((filtered) => (newExpanded[filtered] = true));
            if (!isReporting) {
                if (filteredKeys.length > 0) {
                    const idx = Number(filteredKeys[0]);
                    const ticket = actionplansData?.data[idx];
                    if (ticket) {
                        pushHistory(ticket.ruuid);
                    }
                } else {
                    pushHistory();
                }
            }
            setExpanded(newExpanded);
        }
    };

    const table = useReactTable({
        columns,
        data: !data ? actionplansData?.data ?? [] : data,
        state: {
            columnFilters,
            sorting,
            pagination,
            expanded,
        },
        getCoreRowModel: getCoreRowModel(),
        getRowCanExpand: () => true,
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        onExpandedChange: setExpandHandler,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
    });

    useImperativeHandle(ref, () => ({
        getTableInstance: () => table,
    }));

    useEffect(() => {
        const key = autoOpenIndex.toString();
        if (autoOpenIndex >= 0) {
            const newExpanded: Record<string, boolean> = { [key]: true };
            setExpanded(newExpanded);
        }
    }, [autoOpenIndex]);

    useEffect(() => {
        const length = Object.keys(expanded).length;
        if (length > 0) {
            // Only call refreshSelectedActionPlan for auto-opening from URL parameters
            // Don't call it for manual row expansions to prevent pagination reset
            if (autoOpenIndex >= 0) {
                const filteredActionPlans = table.getFilteredRowModel().rows.map((row) => row.original);
                refreshSelectedActionPlan(filteredActionPlans);
            }
        } else {
            if (initialExpander) {
                setInitialExpander(false);
            } else {
                handleCancel();
            }
        }
    }, [expanded]);

    useEffect(() => {
        const filteredRows = actionplansData?.result?.total_count || 0;

        if (filteredRows !== numOfTickets) {
            handleFiltersChange(filteredRows);
        }
    }, [actionplansData]);

    const getSortName = (id: string): string => {
        const sortMap = {
            created: "enflux_created",
            dashboard: "dashboard_name",
            name: "insight",
            created_by_name: "created_by",
        };
        return sortMap[id] || id;
    };

    // sorting
    useEffect(() => {
        if (sorting.length > 0) {
            query.set("sort", getSortName(sorting[0].id));
            query.set("order", sorting[0].desc ? "desc" : "asc");
            navigate({
                pathname: location.pathname,
                search: query.toString(),
            });
        } else {
            query.delete("sort");
            query.delete("order");
            navigate({
                pathname: location.pathname,
                search: query.toString(),
            });
        }
    }, [sorting]);

    const getCancelHandler = (row: RowInfo<Ticket>) => {
        const handler = row.getToggleExpandedHandler();
        return () => {
            handler();
            handleCancel();
        };
    };

    const getSaveDoneHandler = (row: RowInfo<Ticket>) => {
        const handler = row.getToggleExpandedHandler();

        return () => {
            handleSaveDone();
            handler();
        };
    };

    const onClickRowHandler = (row: RowInfo<Ticket>, event: MouseEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        event.stopPropagation();
        row.getToggleExpandedHandler()();
    };

    const renderSubComponent = (row: RowInfo<Ticket>) => {
        return <TicketDetailForm dashboard={dashboard} currentTicket={row.original} refreshView={refreshView} isPreview={isReporting || false} onUpdate={handleActionplanReload} onCancel={getCancelHandler(row)} onSaveDone={getSaveDoneHandler(row)} onCloseDone={handleCloseDone} />;
    };

    return (
        <div className="list">
            <Table>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th key={header.id} id={header.id}>
                                    {header.isPlaceholder ? null : (
                                        <>
                                            <div className={header.column.getCanSort() ? "d-flex align-items-center gap-2 cursor-pointer " + (header.column.getIsSorted() || "") : ""} onClick={header.column.getToggleSortingHandler()}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}

                                                {header.column.getIsSorted() === "asc" ? <ArrowUp /> : header.column.getIsSorted() === "desc" ? <ArrowDown /> : <ArrowDownUp />}
                                            </div>
                                        </>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <Fragment key={row.id}>
                            <tr key={row.id} onClick={(event) => onClickRowHandler(row, event)} className={row.getIsExpanded() ? "expanded" : "expandable"}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                ))}
                            </tr>
                            {row.getIsExpanded() && (
                                <tr>
                                    <td colSpan={row.getVisibleCells().length}>{renderSubComponent(row)}</td>
                                </tr>
                            )}
                        </Fragment>
                    ))}
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                No Results
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </Table>
            <PagePagination
                table={table}
                totalPages={totalPages}
                page={pagination.pageIndex}
                pageSize={pageSize}
                setCurrentPage={page => {
                    query.set("page", (page+1).toString());
                    navigate({
                        pathname: location.pathname,
                        search: query.toString(),
                    });
                    updateCurrentPage(page);
                }}
                setPageSize={pageSize => {
                    updatePageSize(pageSize);
                    query.set("page_size", pageSize.toString());
                    navigate({
                        pathname: location.pathname,
                        search: query.toString(),
                    });
                }} />
        </div>
    );
});

ActionPlanReactTable.displayName = "ActionPlanReactTable";
